import { useNavigate } from "react-router-dom";
import { Target, Shield, Users, Leaf, ArrowRight } from "lucide-react";
import { useEffect, useRef } from "react";

const OurMission = () => {
  // const [isExpanded, setIsExpanded] = useState(false);

  const missionRef = useRef(null);

  useEffect(() => {
    setTimeout(() => {
      if (missionRef.current) {
        missionRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, 1000);
  });

  const navigate = useNavigate();

  const missionPoints = [
    {
      icon: <Target className="w-6 h-6" />,
      title: "Promote Golf",
      description:
        "Fostering the growth and love for the sport in our community",
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "World-Class Standards",
      description: "Maintaining excellence in facilities and services",
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Inclusive Environment",
      description: "Welcoming all members and guests with open arms",
    },
    {
      icon: <Leaf className="w-6 h-6" />,
      title: "Environmental Stewardship",
      description: "Preserving the natural beauty of our surroundings",
    },
  ];

  return (
    <div className="font-sans">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center text-white overflow-hidden">
        {/* Background Image with Gradient Overlay */}
        <div className="absolute inset-0">
          <img
            src="https://res.cloudinary.com/dxdvcplbl/image/upload/v1754842635/DJI_20250810155002_0040_D-min_yyib3k.jpg"
            alt="RGCC Mission"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/80 via-emerald-800/60 to-teal-900/40"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-4xl px-4 md:px-8 text-center">
          <div className="inline-flex items-center gap-2 mb-6">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500/20 to-teal-500/20 backdrop-blur-sm border border-emerald-400/30 flex items-center justify-center">
              <Target className="w-6 h-6 text-emerald-300" />
            </div>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
            Our Guiding{" "}
            <span className="relative inline-block">
              <span className="relative z-10 bg-gradient-to-r from-emerald-300 to-teal-300 bg-clip-text text-transparent">
                Mission
              </span>
              {/* <span className="absolute bottom-2 left-0 w-full h-3 bg-gradient-to-r from-emerald-500/30 to-teal-500/30 -rotate-1"></span> */}
            </span>
          </h1>

          <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto leading-relaxed">
            Driving excellence, fostering community, and preserving nature
            through golf
          </p>
        </div>
      </section>

      {/* Mission Content */}
      <div className="bg-gradient-to-br from-emerald-50/30 via-white to-teal-50/30">
        <section className="py-16 md:py-24 px-4 md:px-8 max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Image Column */}
            <div className="relative group order-2 lg:order-1">
              <div className="absolute -inset-4 bg-gradient-to-r from-emerald-400 to-teal-400 rounded-2xl blur-xl opacity-20 group-hover:opacity-30 transition-opacity duration-500"></div>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://res.cloudinary.com/dxdvcplbl/image/upload/v1754842635/DJI_20250810155002_0040_D-min_yyib3k.jpg"
                  alt="RGCC Mission Vision"
                  className="w-full h-auto transform group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/20 to-transparent"></div>
              </div>
            </div>

            {/* Text Column */}
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 mb-4">
                <span className="w-8 h-0.5 bg-gradient-to-r from-emerald-500 to-teal-500"></span>
                <span className="text-sm font-semibold tracking-widest text-emerald-600 uppercase">
                  Our Commitment
                </span>
                <span className="w-8 h-0.5 bg-gradient-to-r from-teal-500 to-emerald-500"></span>
              </div>

              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Our Mission Statement
              </h2>

              <div
                ref={missionRef}
                className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl p-6 md:p-8 mb-8 border border-emerald-100"
              >
                <blockquote className="text-2xl md:text-3xl font-medium text-gray-800 leading-relaxed italic">
                  "To promote golf by providing a world-class, serene, and
                  inclusive environment, fostering sportsmanship, camaraderie,
                  and excellence among our members and guests while preserving
                  the natural beauty of our surroundings."
                </blockquote>
              </div>

              <p className="text-gray-600 leading-relaxed mb-8">
                Our mission is the foundation of everything we do at Rangpur
                Golf & Country Club. It guides our decisions, shapes our
                facilities, and defines our commitment to creating an
                exceptional golfing experience for everyone who walks through
                our gates.
              </p>

              {/* Mission Points */}
              <div className="space-y-6">
                {missionPoints.map((point, index) => (
                  <div key={index} className="flex items-start gap-4 group">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                      <div className="text-emerald-600">{point.icon}</div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-1">
                        {point.title}
                      </h3>
                      <p className="text-gray-600">{point.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Call to Action */}
              <div className="mt-12 pt-8 border-t border-gray-100">
                <div className="flex flex-col sm:flex-row items-center gap-6">
                  <p className="text-gray-600 flex-1">
                    Ready to experience our commitment to excellence firsthand?
                  </p>
                  <button
                    onClick={() => navigate("/membership")}
                    className="inline-flex items-center gap-2 px-8 py-3 rounded-full font-bold text-white bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 transition-all duration-300 transform hover:-translate-y-0.5 shadow-lg hover:shadow-xl"
                  >
                    Join Our Community
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Impact Section */}
          <div className="mt-20 bg-white rounded-2xl p-8 md:p-12 border border-gray-100 shadow-sm">
            <div className="text-center max-w-4xl mx-auto">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
                Living Our Mission Every Day
              </h3>
              <div className="grid md:grid-cols-3 gap-8 mt-8">
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl font-bold text-emerald-600">
                      70+
                    </span>
                  </div>
                  <p className="font-semibold text-gray-900">
                    Tournaments Hosted
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-teal-50 to-emerald-50 flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl font-bold text-teal-600">
                      245+
                    </span>
                  </div>
                  <p className="font-semibold text-gray-900">Active Members</p>
                </div>
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl font-bold text-emerald-600">
                      9
                    </span>
                  </div>
                  <p className="font-semibold text-gray-900">
                    Championship Holes
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default OurMission;
